import { reducerWithInitialState } from 'typescript-fsa-reducers';
import { NetworkStatus } from '@cbridge-utils/network';
import { MarkoutAction } from './markout.action';
import { EMPTY_FILTER_OPTIONS, markoutInitialState } from './markout.constant';
import { IBasicDashboardInfo, IMarkoutStore } from './markout.interface';
import { makeFilterOptionsTargetKey } from './_helpers/makeFilterOptionsTargetKey';
import { isFilteredDataRequest } from './_helpers/isFilteredDataRequest';

// The dashboard revision bumps when a list response brings current-day data
// in (no maxRisk → maxRisk) after an earlier response had none: the page's
// mount-time /dashboard request went out before the data existed, so the
// Current Day subscribers refetch on it (LB-5232). The first response of a
// session never bumps — the mount-time request is in flight anyway.
const nextDashboardDataRevision = (state: IMarkoutStore, dashboard: IBasicDashboardInfo | null): number => (
  state.reportsNetworkStatus !== NetworkStatus.None
    && state.dashboard?.maxRisk === undefined
    && dashboard?.maxRisk !== undefined
    ? state.dashboardDataRevision + 1
    : state.dashboardDataRevision
);

export const markoutReducer = reducerWithInitialState(markoutInitialState)
  .case(MarkoutAction.loadReports.started, (state) => ({
    ...state,
    reportsNetworkStatus: NetworkStatus.Started,
  }))
  .case(MarkoutAction.loadReports.done, (state, { result }) => ({
    ...state,
    reports: result.reports,
    dashboard: result.dashboard,
    dashboardDataRevision: nextDashboardDataRevision(state, result.dashboard),
    reportsNetworkStatus: NetworkStatus.Done,
    reportsRetrying: false,
  }))
  .case(MarkoutAction.loadReports.failed, (state) => ({
    ...state,
    reportsNetworkStatus: NetworkStatus.Failed,
    reportsRetrying: false,
  }))
  // The sidebar's own report fetch carries the current-day dashboard and marks
  // the list as loaded, so a separate dashboard-only request is not needed.
  // Keep the existing dashboard when a (filtered) response omits it.
  .case(MarkoutAction.dashboardLoaded, (state, { dashboard }) => ({
    ...state,
    dashboard: dashboard ?? state.dashboard,
    dashboardDataRevision: nextDashboardDataRevision(state, dashboard ?? state.dashboard),
    reportsNetworkStatus: NetworkStatus.Done,
    reportsRetrying: false,
  }))
  .case(MarkoutAction.mergeReports, (state, { reports }) => {
    const byId = new Map(state.reports.map((r) => [r.id, r]));
    reports.forEach((r) => byId.set(r.id, r));
    return { ...state, reports: Array.from(byId.values()) };
  })
  .case(MarkoutAction.loadReportData.started, (state) => ({
    ...state,
    dataNetworkStatus: NetworkStatus.Started,
  }))
  .case(MarkoutAction.loadReportData.done, (state, { params, result }) => ({
    ...state,
    data: result,
    dataTarget: makeFilterOptionsTargetKey(params.reportId, params.jobId),
    dataFiltered: isFilteredDataRequest(params),
    dataNetworkStatus: NetworkStatus.Done,
    dataRetrying: false,
  }))
  .case(MarkoutAction.loadReportData.failed, (state) => ({
    ...state,
    dataNetworkStatus: NetworkStatus.Failed,
    dataRetrying: false,
  }))
  .case(MarkoutAction.clearReportData, (state) => ({
    ...state,
    data: null,
    dataTarget: null,
    dataFiltered: false,
    dataNetworkStatus: NetworkStatus.None,
    dataRetrying: false,
  }))
  .case(MarkoutAction.loadAccountSymbols.started, (state, params) => ({
    ...state,
    symbolsTarget: { serverId: params.serverId, accountId: params.accountId },
    symbolsNetworkStatus: NetworkStatus.Started,
  }))
  .case(MarkoutAction.loadAccountSymbols.done, (state, { params, result }) => {
    if (
      state.symbolsTarget === null
      || state.symbolsTarget.serverId !== params.serverId
      || state.symbolsTarget.accountId !== params.accountId
    ) {
      return state;
    }
    return {
      ...state,
      symbols: result,
      symbolsNetworkStatus: NetworkStatus.Done,
    };
  })
  .case(MarkoutAction.loadAccountSymbols.failed, (state) => ({
    ...state,
    symbolsNetworkStatus: NetworkStatus.Failed,
  }))
  .case(MarkoutAction.clearAccountSymbols, (state) => ({
    ...state,
    symbols: null,
    symbolsTarget: null,
    symbolsNetworkStatus: NetworkStatus.None,
  }))
  // Options are cached per report job: a DONE job's options never change, so a
  // report visited earlier in the session is not requested again.
  .case(MarkoutAction.loadFilterOptions.started, (state, params) => {
    const key = makeFilterOptionsTargetKey(params.reportId, params.jobId);
    return {
      ...state,
      filterOptionsByTarget: {
        ...state.filterOptionsByTarget,
        [key]: {
          options: state.filterOptionsByTarget[key]?.options ?? EMPTY_FILTER_OPTIONS,
          networkStatus: NetworkStatus.Started,
        },
      },
    };
  })
  .case(MarkoutAction.loadFilterOptions.done, (state, { params, result }) => ({
    ...state,
    filterOptionsByTarget: {
      ...state.filterOptionsByTarget,
      [makeFilterOptionsTargetKey(params.reportId, params.jobId)]: { options: result, networkStatus: NetworkStatus.Done },
    },
  }))
  .case(MarkoutAction.loadFilterOptions.failed, (state, { params }) => {
    const key = makeFilterOptionsTargetKey(params.reportId, params.jobId);
    return {
      ...state,
      filterOptionsByTarget: {
        ...state.filterOptionsByTarget,
        [key]: {
          options: state.filterOptionsByTarget[key]?.options ?? EMPTY_FILTER_OPTIONS,
          networkStatus: NetworkStatus.Failed,
        },
      },
    };
  })
  .case(MarkoutAction.loadFormSymbols.started, (state, params) => ({
    ...state,
    formSymbolsByServer: {
      ...state.formSymbolsByServer,
      [params.serverId]: {
        symbols: state.formSymbolsByServer[params.serverId]?.symbols ?? [],
        networkStatus: NetworkStatus.Started,
      },
    },
  }))
  .case(MarkoutAction.loadFormSymbols.done, (state, { params, result }) => ({
    ...state,
    formSymbolsByServer: {
      ...state.formSymbolsByServer,
      [params.serverId]: { symbols: result.symbols, networkStatus: NetworkStatus.Done },
    },
  }))
  .case(MarkoutAction.loadFormSymbols.failed, (state, { params }) => ({
    ...state,
    formSymbolsByServer: {
      ...state.formSymbolsByServer,
      [params.serverId]: {
        symbols: state.formSymbolsByServer[params.serverId]?.symbols ?? [],
        networkStatus: NetworkStatus.Failed,
      },
    },
  }))
  .case(MarkoutAction.createReport.done, (state) => ({
    ...state,
    reportsListRevision: state.reportsListRevision + 1,
  }))
  .case(MarkoutAction.stopReport.done, (state) => ({
    ...state,
    reportsListRevision: state.reportsListRevision + 1,
  }))
  .case(MarkoutAction.retryReport.done, (state) => ({
    ...state,
    reportsListRevision: state.reportsListRevision + 1,
  }))
  // A reload requested while the list is failed is the stub's Retry: the stub
  // stays up with a loading button until the refetch settles. Polling reloads
  // (list loaded) never mark a retry.
  .case(MarkoutAction.reloadReportList, (state) => ({
    ...state,
    reportsListRevision: state.reportsListRevision + 1,
    reportsRetrying: state.reportsNetworkStatus === NetworkStatus.Failed,
  }))
  // A data reload requested while the load is failed is the stub's Retry: the
  // stub stays up with a loading button until the refetch settles. The
  // no-result stub's Clear Filters reloads the same way — at once, past the
  // subscriber's filter debounce — without marking a retry (LB-5354).
  .case(MarkoutAction.reloadReportData, (state) => ({
    ...state,
    reportDataRevision: state.reportDataRevision + 1,
    dataRetrying: state.dataNetworkStatus === NetworkStatus.Failed,
  }))
  .case(MarkoutAction.destroy, () => ({ ...markoutInitialState }));
